import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { X } from 'src/components/common/icons';
import { Field, SelectField } from './Master';
import { ITableConfig, ITableColumn, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Pager from 'src/components/common/table/Pager';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

const PAGE_SIZE = 25;

const FORMAT_TYPE_OPTIONS = [
    { value: 'T', label: 'Tract' },
    { value: 'M', label: 'Map' },
    { value: 'R', label: 'Right of Way' },
    { value: 'L', label: 'ROW Line' },
];

const CLIENT_LEASE_FORMAT_OPTIONS = [
    { value: 'N', label: 'None' },
    { value: 'A', label: 'Artesian' },
    { value: 'L', label: 'LandPro' },
];

interface IClient {
    clNo: number;
    name1: string | null;
    name2: string | null;
    name3: string | null;
    name4: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    fax: string | null;
    email: string | null;
    country: string | null;
}

interface IProspect {
    prId: number;
    clNo: number | null;
    prNo: string | null;
    prName: string | null;
    clLman: string | null;
    tdLman: string | null;
    prCnty: string | null;
    prState: string | null;
    tdName: string | null;
    clLmname: string | null;
    formatType: string | null;
    clientCode: string | null;
    clientState: string | null;
    clientProspectNo: string | null;
    clientLeaseFormat: string | null;
    clientLeasePrefix: string | null;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

type TabType = 'clients' | 'prospects';
type ViewMode = 'list' | 'form';
type ConversionDialogVariant = 'info' | 'success' | 'error' | 'confirm';

interface IConversionDialogState {
    variant: ConversionDialogVariant;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm?: () => void;
}

const ClientsProspectsModal: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, post, put, del, response, loading } = useFetch();

    const [activeTab, setActiveTab] = useState<TabType>('clients');
    const [viewMode, setViewMode] = useState<ViewMode>('list');

    // --- State: Clients ---
    const [clientsData, setClientsData] = useState<PagedResultSet<IClient> | undefined>();
    const [clientPageNumber, setClientPageNumber] = useState(1);
    const [clientStateFilter, setClientStateFilter] = useState('');
    const [clientSearchFilter, setClientSearchFilter] = useState('');
    const [shouldReloadClients, setShouldReloadClients] = useState(true);

    // --- State: Prospects ---
    const [prospectsData, setProspectsData] = useState<PagedResultSet<IProspect> | undefined>();
    const [prospectPageNumber, setProspectPageNumber] = useState(1);
    const [prospectStateFilter, setProspectStateFilter] = useState('');
    const [prospectCountyFilter, setProspectCountyFilter] = useState('');
    const [prospectSearchFilter, setProspectSearchFilter] = useState('');
    const [prospectClientNumberFilter, setProspectClientNumberFilter] = useState(''); // NEW FILTER
    const [shouldReloadProspects, setShouldReloadProspects] = useState(true);

    // --- Form States ---
    const [editingClient, setEditingClient] = useState<IClient | null>(null);
    const [editingProspect, setEditingProspect] = useState<IProspect | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isConvertingProject, setIsConvertingProject] = useState(false);
    const [conversionDialog, setConversionDialog] = useState<IConversionDialogState | null>(null);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    // --- Lookup States ---
    const [statesList, setStatesList] = useState<{ value: string; label: string }[]>([]);
    const [countiesList, setCountiesList] = useState<{ value: string; label: string }[]>([]);
    const [filterCountiesList, setFilterCountiesList] = useState<{ value: string; label: string }[]>([]);

    // Initial Data loads
    useEffect(() => {
        let isMounted = true;
        const loadStates = async (): Promise<void> => {
            const res = await get('/v1.0/leases/states');
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setStatesList(res.map((s: { stCode: string; abbr: string }) => ({ value: s.stCode, label: s.abbr })));
                }
            }
        };
        if (isOpen) {
            loadStates();
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Load Counties dynamically when state selected in Prospect FORM
    useEffect(() => {
        let isMounted = true;
        const loadCounties = async (stateCode: string): Promise<void> => {
            const res = await get(`/v1.0/leases/states/${stateCode}/counties`);
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setCountiesList(res.map((r: { county: string }) => ({ value: r.county, label: r.county })));
                } else {
                    setCountiesList([]);
                }
            }
        };
        if (viewMode === 'form' && activeTab === 'prospects' && editingProspect && editingProspect.prState) {
            loadCounties(editingProspect.prState);
        } else {
            setCountiesList([]);
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, activeTab, editingProspect?.prState]);

    // Load Counties dynamically when state selected in Prospect Toolbar
    useEffect(() => {
        let isMounted = true;
        const loadFilterCounties = async (stateCode: string): Promise<void> => {
            const res = await get(`/v1.0/leases/states/${stateCode}/counties`);
            if (isMounted) {
                if (res && Array.isArray(res)) {
                    setFilterCountiesList(res.map((r: { county: string }) => ({ value: r.county, label: r.county })));
                } else {
                    setFilterCountiesList([]);
                }
            }
        };
        if (viewMode === 'list' && activeTab === 'prospects' && prospectStateFilter) {
            loadFilterCounties(prospectStateFilter);
        } else {
            setFilterCountiesList([]);
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, activeTab, prospectStateFilter]);

    // Load Clients
    useEffect(() => {
        const loadClients = async (): Promise<void> => {
            const skip = (clientPageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (clientStateFilter.trim()) {
                params.append('state', clientStateFilter.trim());
            }
            if (clientSearchFilter.trim()) {
                params.append('search', clientSearchFilter.trim());
            }

            const data = await get(`/v1.0/leases/admin/clients?${params.toString()}`);
            if (data && typeof data.totalCount !== 'undefined') {
                const pagedResult = new PagedResultSet<IClient>();
                pagedResult.items = data.items.map((c: IClient) => ({ ...c, id: c.clNo.toString() }));
                pagedResult.totalRecords = data.totalCount;
                pagedResult.pageNumber = clientPageNumber;
                pagedResult.pageSize = PAGE_SIZE;
                setClientsData(pagedResult);
            }
        };

        if (isOpen && activeTab === 'clients' && shouldReloadClients) {
            loadClients();
            setShouldReloadClients(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeTab, shouldReloadClients, clientPageNumber]);

    // Load Prospects
    useEffect(() => {
        const loadProspects = async (): Promise<void> => {
            const skip = (prospectPageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (prospectStateFilter.trim()) {
                params.append('state', prospectStateFilter.trim());
            }
            if (prospectCountyFilter.trim()) {
                params.append('county', prospectCountyFilter.trim());
            }
            if (prospectSearchFilter.trim()) {
                params.append('search', prospectSearchFilter.trim());
            }
            // NEW FILTER PARAM
            if (prospectClientNumberFilter.trim()) {
                params.append('clientNumber', prospectClientNumberFilter.trim());
            }

            const data = await get(`/v1.0/leases/admin/prospects?${params.toString()}`);
            if (data && typeof data.totalCount !== 'undefined') {
                const pagedResult = new PagedResultSet<IProspect>();
                pagedResult.items = data.items.map((p: IProspect) => ({ ...p, id: p.prId.toString() }));
                pagedResult.totalRecords = data.totalCount;
                pagedResult.pageNumber = prospectPageNumber;
                pagedResult.pageSize = PAGE_SIZE;
                setProspectsData(pagedResult);
            }
        };

        if (isOpen && activeTab === 'prospects' && shouldReloadProspects) {
            loadProspects();
            setShouldReloadProspects(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeTab, shouldReloadProspects, prospectPageNumber]);

    const resetToSearch = (): void => {
        setViewMode('list');
        setEditingClient(null);
        setEditingProspect(null);
        setIsCreating(false);

        if (activeTab === 'clients') {
            setShouldReloadClients(true);
        } else {
            setShouldReloadProspects(true);
        }
    };

    const handleSearch = (): void => {
        if (activeTab === 'clients') {
            setClientPageNumber(1);
            setShouldReloadClients(true);
        } else {
            setProspectPageNumber(1);
            setShouldReloadProspects(true);
        }
    };

    const handleClearFilters = (): void => {
        if (activeTab === 'clients') {
            setClientStateFilter('');
            setClientSearchFilter('');
            setClientPageNumber(1);
            setShouldReloadClients(true);
        } else {
            setProspectStateFilter('');
            setProspectCountyFilter('');
            setProspectSearchFilter('');
            setProspectClientNumberFilter('');
            setProspectPageNumber(1);
            setShouldReloadProspects(true);
        }
    };

    const handlePageChange = (newPageNumber: number): void => {
        if (activeTab === 'clients') {
            setClientPageNumber(newPageNumber);
            setShouldReloadClients(true);
        } else {
            setProspectPageNumber(newPageNumber);
            setShouldReloadProspects(true);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleAddClick = (): void => {
        setIsCreating(true);
        if (activeTab === 'clients') {
            setEditingClient({
                clNo: 0,
                name1: '',
                name2: '',
                name3: '',
                name4: '',
                city: '',
                state: '',
                zip: '',
                phone: '',
                fax: '',
                email: '',
                country: '',
            });
        } else {
            setEditingProspect({
                prId: 0,
                clNo: null,
                prNo: '',
                prName: '',
                clLman: '',
                tdLman: '',
                prCnty: '',
                prState: '',
                tdName: '',
                clLmname: '',
                formatType: '',
                clientCode: '',
                clientState: '',
                clientProspectNo: '',
                clientLeaseFormat: '',
                clientLeasePrefix: '',
            });
        }
        setViewMode('form');
    };

    const handleRowClick = (id: string | number): void => {
        if (activeTab === 'clients') {
            if (clientsData && clientsData.items) {
                const selected = clientsData.items.find((c) => c.clNo.toString() === id.toString());
                if (selected) {
                    setIsCreating(false);
                    setEditingClient(selected);
                    setViewMode('form');
                }
            }
        } else {
            if (prospectsData && prospectsData.items) {
                const selected = prospectsData.items.find((p) => p.prId.toString() === id.toString());
                if (selected) {
                    setIsCreating(false);
                    setEditingProspect(selected);
                    setViewMode('form');
                }
            }
        }
    };

    const handleSave = async (): Promise<void> => {
        setRequestError(null);
        if (activeTab === 'clients' && editingClient) {
            if (!editingClient.clNo || !editingClient.name1) {
                alert('Client Number and Name are required.');
                return;
            }
            if (editingClient.clNo > 99999) {
                alert('Client Number is too large. It cannot exceed 5 digits (99999).');
                return;
            }
            if (isCreating) {
                await post('/v1.0/leases/admin/clients', editingClient);
            } else {
                await put('/v1.0/leases/admin/clients', editingClient);
            }

            if (response.ok) {
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to save Client record.' }, response.status));
            }
        } else if (activeTab === 'prospects' && editingProspect) {
            if (!editingProspect.prNo || !editingProspect.prName) {
                alert('Prospect Number and Name are required.');
                return;
            }
            if (editingProspect.clNo !== null && editingProspect.clNo > 99999) {
                alert('Client Database Number is too large. It cannot exceed 5 digits (99999).');
                return;
            }
            if (isCreating) {
                await post('/v1.0/leases/admin/prospects', editingProspect);
            } else {
                await put('/v1.0/leases/admin/prospects', editingProspect);
            }

            if (response.ok) {
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to save Prospect record.' }, response.status));
            }
        }
    };

    const handleDelete = async (): Promise<void> => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        if (activeTab === 'clients' && editingClient) {
            setRequestError(null);
            await del(`/v1.0/leases/admin/clients/${editingClient.clNo}`);
            if (response.ok) {
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to delete Client record.' }, response.status));
            }
        } else if (activeTab === 'prospects' && editingProspect) {
            setRequestError(null);
            await del(`/v1.0/leases/admin/prospects/${editingProspect.prId}`);
            if (response.ok) {
                resetToSearch();
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to delete Prospect record.' }, response.status));
            }
        }
    };

    const runProjectConversion = async (): Promise<void> => {
        if (!editingProspect?.clNo || !editingProspect.prNo) {
            setConversionDialog({
                variant: 'info',
                title: 'Conversion',
                message: 'Client Database Number and Project Number are required before running Conversion.',
            });
            return;
        }

        const projectNumber = Number(editingProspect.prNo);
        if (!Number.isInteger(projectNumber)) {
            setConversionDialog({
                variant: 'info',
                title: 'Conversion',
                message: 'Project Number must be numeric before running Conversion.',
            });
            return;
        }

        setIsConvertingProject(true);

        try {
            const result = await post('/v1.0/leases/admin/projects/sync-project', {
                clientNumber: editingProspect.clNo,
                projectNumber,
            });

            if (!response.ok) {
                setConversionDialog({
                    variant: 'error',
                    title: 'Conversion Failed',
                    message: result?.message || result || 'Conversion failed.',
                });
                return;
            }

            if (result?.status === 0) {
                setConversionDialog({
                    variant: 'success',
                    title: 'Conversion Complete',
                    message: result?.message || 'Conversion completed successfully.',
                });
                return;
            }

            setConversionDialog({
                variant: 'error',
                title: 'Conversion Unsuccessful',
                message: result?.message || 'Conversion was unsuccessful.',
            });
        } catch {
            setConversionDialog({
                variant: 'error',
                title: 'Conversion Error',
                message: 'An error occurred while running Conversion.',
            });
        } finally {
            setIsConvertingProject(false);
        }
    };

    const handleProjectConversion = (): void => {
        if (!editingProspect?.clNo || !editingProspect.prNo) {
            setConversionDialog({
                variant: 'info',
                title: 'Conversion',
                message: 'Client Database Number and Project Number are required before running Conversion.',
            });
            return;
        }

        setConversionDialog({
            variant: 'confirm',
            title: 'Run Conversion?',
            message: `Run ownership conversion for client ${editingProspect.clNo}, project ${editingProspect.prNo}?`,
            confirmText: 'Run Conversion',
            cancelText: 'Cancel',
            onConfirm: () => {
                setConversionDialog(null);
                void runProjectConversion();
            },
        });
    };

    // --- Action Handlers ---
    const handleShowProspectsClick = (e: React.MouseEvent, clNo: number): void => {
        e.stopPropagation(); // Prevents the row click from opening the edit form

        setActiveTab('prospects');
        setViewMode('list');

        // Reset all other prospect filters and set the exact client number
        setProspectStateFilter('');
        setProspectCountyFilter('');
        setProspectSearchFilter('');
        setProspectClientNumberFilter(clNo.toString());
        setProspectPageNumber(1);

        setShouldReloadProspects(true);
    };

    // --- Table Configurations ---
    // const clientsColumns: ITableColumn[] = [
    //     { name: 'Number', mapping: 'clNo' },
    //     { name: 'Name', mapping: 'name1' },
    //     { name: 'Address', mapping: 'name2' },
    //     { name: 'City', mapping: 'city' },
    //     { name: 'State', mapping: 'state' },
    //     {
    //         name: '', // Empty header for actions
    //         mapping: 'clNo',
    //         // The Table component accepts a format function that can return React nodes if cast appropriately
    //         // However, your standard ITableColumn might strictly expect a string format.
    //         // If `format` strictly expects a string in your Table implementation, we'll need to render it as a custom cell in the row
    //         // To ensure compatibility without breaking your Table.tsx, we pass an ID string and render it cleanly.
    //     }
    // ];

    // const clientsTableConfig: ITableConfig = {
    //     rowCount: clientsData && clientsData.items ? clientsData.items.length : 0,
    //     pageNumber: clientPageNumber,
    //     idField: 'clNo',
    //     columns: clientsColumns
    // };

    const prospectColumns: ITableColumn[] = [
        { name: 'Number', mapping: 'prNo' },
        { name: 'Name', mapping: 'prName' },
        { name: 'County', mapping: 'prCnty' },
        { name: 'State', mapping: 'prState' },
        { name: 'Client Number', mapping: 'clNo' },
    ];

    const prospectsTableConfig: ITableConfig = {
        rowCount: prospectsData && prospectsData.items ? prospectsData.items.length : 0,
        pageNumber: prospectPageNumber,
        idField: 'prId',
        columns: prospectColumns,
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
                {/* Shared Generic Table Fixes */}
                <style>{`
                    .modal-table-container {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        border: none !important;
                    }
                    .modal-table-container table {
                        width: 100%;
                        border-collapse: collapse;
                        text-align: left;
                        margin-bottom: 0 !important;
                        border: none !important;
                        box-shadow: none !important;
                    }
                    .modal-table-container th, 
                    .modal-table-container td {
                        border-left: none !important;
                        border-right: none !important;
                    }
                    .modal-table-container th {
                        background-color: #FFFFFF;
                        color: #6B7280;
                        font-weight: 600;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        padding: 0.75rem 1rem;
                        border-bottom: 2px solid #E5E7EB !important;
                        border-top: none !important;
                        white-space: nowrap;
                        position: sticky;
                        top: 0;
                        z-index: 10;
                    }
                    .modal-table-container td {
                        padding: 0.75rem 1rem;
                        font-size: 0.875rem;
                        color: #111827;
                        border-bottom: 1px solid #F3F4F6 !important;
                        white-space: nowrap;
                    }
                    .modal-table-container tbody tr:last-child td {
                        border-bottom: none !important;
                    }
                    .modal-table-container tbody tr {
                        cursor: pointer;
                        transition: background-color 0.15s ease;
                    }
                    .modal-table-container tbody tr:hover td {
                        background-color: #F9FAFB !important;
                    }
                    .modal-table-container::-webkit-scrollbar {
                        width: 8px;
                    }
                    .modal-table-container::-webkit-scrollbar-track {
                        background: transparent;
                        margin-top: 40px; 
                    }
                    .modal-table-container::-webkit-scrollbar-thumb {
                        background-color: #D1D5DB;
                        border-radius: 10px;
                    }
                    .modal-table-container::-webkit-scrollbar-thumb:hover {
                        background-color: #9CA3AF;
                    }
                    .pager-sticky-wrapper {
                        position: sticky !important;
                        bottom: 0 !important;
                        z-index: 20 !important;
                        background-color: #FFFFFF !important;
                        border-top: 1px solid #E5E7EB !important;
                        padding: 1rem 0 0 0 !important;
                        margin: auto 0 0 0 !important;
                        flex-shrink: 0;
                    }
                `}</style>

                <div style={styles.modalHeader}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <button
                            onClick={() => {
                                setActiveTab('clients');
                                resetToSearch();
                            }}
                            style={activeTab === 'clients' ? styles.tabActive : styles.tabInactive}
                        >
                            Clients
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('prospects');
                                resetToSearch();
                            }}
                            style={activeTab === 'prospects' ? styles.tabActive : styles.tabInactive}
                        >
                            Projects
                        </button>
                    </div>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>
                </div>

                <div style={styles.modalBody}>
                    {/* --- LIST VIEW --- */}
                    {viewMode === 'list' && (
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1.5rem', overflow: 'hidden' }}>
                            {/* Toolbar */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                {activeTab === 'clients' ? (
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <select
                                            style={styles.searchInput}
                                            value={clientStateFilter}
                                            onChange={(e) => setClientStateFilter(e.target.value)}
                                        >
                                            <option value="">All States...</option>
                                            {statesList.map((s) => (
                                                <option key={`client-state-${s.value}`} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                        <LeaseRecordInput
                                            style={{ ...styles.searchInput, width: '200px' }}
                                            placeholder="Search Name or Number..."
                                            value={clientSearchFilter}
                                            onChange={(e) => setClientSearchFilter(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                        <button onClick={handleSearch} style={styles.buttonPrimary}>
                                            Search
                                        </button>
                                        {(clientStateFilter || clientSearchFilter) && (
                                            <button onClick={handleClearFilters} style={styles.buttonSecondary}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <select
                                            style={styles.searchInput}
                                            value={prospectStateFilter}
                                            onChange={(e) => {
                                                setProspectStateFilter(e.target.value);
                                                setProspectCountyFilter('');
                                            }}
                                        >
                                            <option value="">All States...</option>
                                            {statesList.map((s) => (
                                                <option key={`prospect-state-${s.value}`} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            style={{
                                                ...styles.searchInput,
                                                backgroundColor: !prospectStateFilter ? '#F9FAFB' : '#FFFFFF',
                                                cursor: !prospectStateFilter ? 'not-allowed' : 'pointer',
                                                color: !prospectStateFilter ? '#9CA3AF' : '#111827',
                                            }}
                                            value={prospectCountyFilter}
                                            onChange={(e) => setProspectCountyFilter(e.target.value)}
                                            disabled={!prospectStateFilter}
                                        >
                                            <option value="">{prospectStateFilter ? 'All Counties...' : 'Select State First'}</option>
                                            {filterCountiesList.map((c) => (
                                                <option key={`prospect-county-${c.value}`} value={c.value}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </select>

                                        <LeaseRecordInput
                                            style={{ ...styles.searchInput, width: '150px' }}
                                            placeholder="Client Number..."
                                            type="number"
                                            value={prospectClientNumberFilter}
                                            onChange={(e) => setProspectClientNumberFilter(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />

                                        <LeaseRecordInput
                                            style={{ ...styles.searchInput, width: '200px' }}
                                            placeholder="Search Name or Number..."
                                            value={prospectSearchFilter}
                                            onChange={(e) => setProspectSearchFilter(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                        <button onClick={handleSearch} style={styles.buttonPrimary}>
                                            Search
                                        </button>
                                        {(prospectStateFilter || prospectCountyFilter || prospectSearchFilter || prospectClientNumberFilter) && (
                                            <button onClick={handleClearFilters} style={styles.buttonSecondary}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                )}

                                <button onClick={handleAddClick} style={styles.buttonSuccess}>
                                    + Add New Record
                                </button>
                            </div>

                            {/* Tables Container */}
                            <div className="modal-table-container" style={styles.tableWrapper}>
                                {activeTab === 'clients' ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                        <thead>
                                            <tr>
                                                <th>Number</th>
                                                <th>Name</th>
                                                <th>Address</th>
                                                <th>City</th>
                                                <th>State</th>
                                                <th style={{ width: '120px' }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading && (!clientsData || clientsData.items.length === 0) ? (
                                                <tr>
                                                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                                                        Loading...
                                                    </td>
                                                </tr>
                                            ) : clientsData?.items.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                                                        No clients found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                clientsData?.items.map((client) => (
                                                    <tr key={client.clNo} onClick={() => handleRowClick(client.clNo)}>
                                                        <td>{client.clNo}</td>
                                                        <td>{client.name1}</td>
                                                        <td>{client.name2}</td>
                                                        <td>{client.city}</td>
                                                        <td>{client.state}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <button
                                                                style={styles.buttonActionLink}
                                                                onClick={(e) => handleShowProspectsClick(e, client.clNo)}
                                                            >
                                                                Show Projects
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                ) : (
                                    <Table
                                        isLoading={loading && prospectsData === undefined}
                                        tableConfig={prospectsTableConfig}
                                        data={prospectsData}
                                        onRowClick={handleRowClick}
                                        enablePaging={false}
                                    />
                                )}
                            </div>

                            {/* Pagination Footers */}
                            {activeTab === 'clients' && clientsData && clientsData.totalRecords > 0 && (
                                <div className="pager-sticky-wrapper">
                                    <Pager
                                        pageSize={clientsData.pageSize}
                                        pageNumber={clientsData.pageNumber}
                                        currentPageCount={clientsData.items.length}
                                        totalRecords={clientsData.totalRecords}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}

                            {activeTab === 'prospects' && prospectsData && prospectsData.totalRecords > 0 && (
                                <div className="pager-sticky-wrapper">
                                    <Pager
                                        pageSize={prospectsData.pageSize}
                                        pageNumber={prospectsData.pageNumber}
                                        currentPageCount={prospectsData.items.length}
                                        totalRecords={prospectsData.totalRecords}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- FORM VIEW (CLIENTS) --- */}
                    {viewMode === 'form' && activeTab === 'clients' && editingClient && (
                        <div style={styles.formContainer}>
                            <h3 style={styles.formHeader}>{isCreating ? 'Create New Client' : `Edit Client: ${editingClient.name1 || ''}`}</h3>
                            <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: '1fr 1fr' }}>
                                <Field
                                    label="Client Number (Required)"
                                    type="number"
                                    value={editingClient.clNo.toString()}
                                    editable={isCreating}
                                    onChange={(v) => setEditingClient({ ...editingClient, clNo: parseInt(v || '0', 10) })}
                                />
                                <Field
                                    label="Client Name (Required)"
                                    value={editingClient.name1 || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, name1: v })}
                                />
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Address Line 1"
                                        value={editingClient.name2 || ''}
                                        editable={true}
                                        onChange={(v) => setEditingClient({ ...editingClient, name2: v })}
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Address Line 2"
                                        value={editingClient.name3 || ''}
                                        editable={true}
                                        onChange={(v) => setEditingClient({ ...editingClient, name3: v })}
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Address Line 3"
                                        value={editingClient.name4 || ''}
                                        editable={true}
                                        onChange={(v) => setEditingClient({ ...editingClient, name4: v })}
                                    />
                                </div>
                                <Field
                                    label="City"
                                    value={editingClient.city || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, city: v })}
                                />
                                <SelectField
                                    label="State"
                                    value={editingClient.state || ''}
                                    options={statesList}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, state: v })}
                                />
                                <Field
                                    label="Zip Code"
                                    value={editingClient.zip || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, zip: v })}
                                />
                                <Field
                                    label="Country"
                                    value={editingClient.country || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, country: v })}
                                />
                                <Field
                                    label="Phone"
                                    value={editingClient.phone || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, phone: v })}
                                />
                                <Field
                                    label="Fax"
                                    value={editingClient.fax || ''}
                                    editable={true}
                                    onChange={(v) => setEditingClient({ ...editingClient, fax: v })}
                                />
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Email"
                                        value={editingClient.email || ''}
                                        editable={true}
                                        onChange={(v) => setEditingClient({ ...editingClient, email: v })}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- FORM VIEW (PROSPECTS) --- */}
                    {viewMode === 'form' && activeTab === 'prospects' && editingProspect && (
                        <div style={styles.formContainer}>
                            <div style={styles.formHeaderRow}>
                                <h3 style={{ ...styles.formHeader, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                                    {isCreating ? 'Create New Project' : `Edit Project: ${editingProspect.prName || ''}`}
                                </h3>
                                {!isCreating && (
                                    <button
                                        type="button"
                                        onClick={handleProjectConversion}
                                        disabled={isConvertingProject}
                                        style={{
                                            ...styles.buttonSecondary,
                                            opacity: isConvertingProject ? 0.65 : 1,
                                            cursor: isConvertingProject ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {isConvertingProject ? 'Converting...' : 'Conversion'}
                                    </button>
                                )}
                            </div>
                            <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: '1fr 1fr' }}>
                                <Field
                                    label="Project Number (Required)"
                                    value={editingProspect.prNo || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, prNo: v })}
                                />
                                <SelectField
                                    label="Format Type"
                                    value={editingProspect.formatType || ''}
                                    options={FORMAT_TYPE_OPTIONS}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, formatType: v })}
                                />
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field
                                        label="Project Name (Required)"
                                        value={editingProspect.prName || ''}
                                        editable={true}
                                        onChange={(v) => setEditingProspect({ ...editingProspect, prName: v })}
                                    />
                                </div>
                                <SelectField
                                    label="Project State"
                                    value={editingProspect.prState || ''}
                                    options={statesList}
                                    editable={true}
                                    onChange={(v) => {
                                        if (editingProspect.prState !== v) {
                                            setEditingProspect({ ...editingProspect, prState: v, prCnty: '' });
                                        }
                                    }}
                                />
                                <SelectField
                                    label="Project County"
                                    value={editingProspect.prCnty || ''}
                                    options={editingProspect.prState ? countiesList : [{ value: '', label: 'Select State First' }]}
                                    editable={editingProspect.prState !== '' && editingProspect.prState !== null}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, prCnty: v })}
                                />

                                {/* TSD & Client Landman section */}
                                <Field
                                    label="TSD Landman"
                                    value={editingProspect.tdLman || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, tdLman: v })}
                                />
                                <Field
                                    label="TSD Name"
                                    value={editingProspect.tdName || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, tdName: v })}
                                />
                                <Field
                                    label="Client LM"
                                    value={editingProspect.clLman || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clLman: v })}
                                />
                                <Field
                                    label="Client LM Name"
                                    value={editingProspect.clLmname || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clLmname: v })}
                                />

                                {/* Link to Client Table */}
                                <Field
                                    label="Client Database Number (CL_NO)"
                                    type="number"
                                    value={editingProspect.clNo?.toString() || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clNo: v ? parseInt(v, 10) : null })}
                                />
                                <Field
                                    label="Client Project No"
                                    value={editingProspect.clientProspectNo || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clientProspectNo: v })}
                                />

                                {/* Client Formatting */}
                                <Field
                                    label="Client Code"
                                    value={editingProspect.clientCode || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clientCode: v })}
                                />
                                <Field
                                    label="Client State Code"
                                    value={editingProspect.clientState || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clientState: v })}
                                />
                                <SelectField
                                    label="Client Lease Format"
                                    value={editingProspect.clientLeaseFormat || ''}
                                    options={CLIENT_LEASE_FORMAT_OPTIONS}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clientLeaseFormat: v })}
                                />
                                <Field
                                    label="Client Lease Prefix"
                                    value={editingProspect.clientLeasePrefix || ''}
                                    editable={true}
                                    onChange={(v) => setEditingProspect({ ...editingProspect, clientLeasePrefix: v })}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    {viewMode === 'form' ? (
                        <>
                            {!isCreating && (
                                <button onClick={handleDelete} style={styles.buttonDanger}>
                                    Delete Record
                                </button>
                            )}
                            <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
                                <button onClick={resetToSearch} style={styles.buttonSecondary}>
                                    Cancel
                                </button>
                                <button onClick={handleSave} style={styles.buttonSuccess} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Record'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <button onClick={onClose} style={{ ...styles.buttonSecondary, marginLeft: 'auto' }}>
                            Close
                        </button>
                    )}
                </div>

                {conversionDialog && (
                    <ConversionDialog dialog={conversionDialog} isWorking={isConvertingProject} onClose={() => setConversionDialog(null)} />
                )}
            </div>
        </div>
    );
};

const ConversionDialog = ({ dialog, isWorking, onClose }: { dialog: IConversionDialogState; isWorking: boolean; onClose: () => void }) => {
    const tone =
        dialog.variant === 'success' ? '#10B981' : dialog.variant === 'error' ? '#DC2626' : dialog.variant === 'confirm' ? '#F59E0B' : '#4F46E5';

    const handleConfirm = (): void => {
        if (dialog.onConfirm) {
            dialog.onConfirm();
            return;
        }

        onClose();
    };

    return (
        <div style={styles.conversionDialogBackdrop} onClick={dialog.variant === 'confirm' ? undefined : onClose}>
            <div style={styles.conversionDialog} onClick={(e) => e.stopPropagation()}>
                <div style={styles.conversionDialogHeader}>
                    <span style={{ ...styles.conversionDialogStatus, backgroundColor: tone }} />
                    <h3 style={styles.conversionDialogTitle}>{dialog.title}</h3>
                </div>
                <div style={styles.conversionDialogBody}>{dialog.message}</div>
                <div style={styles.conversionDialogFooter}>
                    {dialog.variant === 'confirm' && (
                        <button type="button" style={styles.buttonSecondary} onClick={onClose} disabled={isWorking}>
                            {dialog.cancelText || 'Cancel'}
                        </button>
                    )}
                    <button
                        type="button"
                        style={dialog.variant === 'confirm' ? styles.buttonPrimary : styles.buttonSecondary}
                        onClick={handleConfirm}
                        disabled={isWorking}
                    >
                        {isWorking ? 'Working...' : dialog.confirmText || 'OK'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '90%',
        maxWidth: '1200px',
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        padding: '1.5rem 1.5rem 0',
        borderBottom: '1px solid #E5E7EB',
    },
    tabActive: {
        background: 'none',
        border: 'none',
        outline: 'none',
        borderBottom: '3px solid #4F46E5',
        color: '#111827',
        fontSize: '1.125rem',
        fontWeight: 600,
        paddingBottom: '0.75rem',
        cursor: 'pointer',
    },
    tabInactive: {
        background: 'none',
        border: 'none',
        outline: 'none',
        borderBottom: '3px solid transparent',
        color: '#6B7280',
        fontSize: '1.125rem',
        fontWeight: 500,
        paddingBottom: '0.75rem',
        cursor: 'pointer',
        transition: 'color 0.2s',
    },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
        marginBottom: '0.5rem',
    },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' },
    searchInput: {
        padding: '0.5rem 1rem',
        width: '150px',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '0.875rem',
        outline: 'none',
        backgroundColor: '#FFFFFF',
        color: '#111827',
    },
    tableWrapper: {
        overflowY: 'auto',
        flexGrow: 1,
        backgroundColor: 'white',
        position: 'relative',
    },
    formContainer: { maxWidth: '900px', margin: '0 auto', width: '100%', padding: '1rem' },
    formHeader: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '1.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    formHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalFooter: {
        padding: '1rem 1.5rem',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
        borderBottomLeftRadius: '1rem',
        borderBottomRightRadius: '1rem',
    },
    buttonPrimary: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSecondary: {
        padding: '0.5rem 1.25rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSuccess: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#10B981',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonDanger: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#DC2626',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonActionLink: { background: 'none', border: 'none', color: '#2563EB', fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer', padding: 0 },
    conversionDialogBackdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
    },
    conversionDialog: {
        width: '420px',
        maxWidth: '100%',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 20px 35px rgba(17, 24, 39, 0.24)',
        overflow: 'hidden',
    },
    conversionDialogHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '1rem 1.1rem 0.75rem',
        borderBottom: '1px solid #E5E7EB',
    },
    conversionDialogStatus: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    conversionDialogTitle: {
        margin: 0,
        fontSize: '1rem',
        color: '#111827',
        fontWeight: 600,
    },
    conversionDialogBody: {
        padding: '1rem 1.1rem',
        color: '#374151',
        fontSize: '0.875rem',
        lineHeight: 1.5,
    },
    conversionDialogFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.6rem',
        padding: '0.85rem 1.1rem',
        backgroundColor: '#F9FAFB',
        borderTop: '1px solid #E5E7EB',
    },
};

export default ClientsProspectsModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ClientsProspectsModal.tsx